import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { isValidId } from "../u2000Core/tools/U2000IdUtils";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3110";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Aligné backend `tsTaxRateComponentApplicationMode` (U3110). */
export type TsTaxRateComponentApplicationMode = "AdditiveOnPrevious" | "MultiplicativeOnPrevious";

export const U3110_APPLICATION_MODES: TsTaxRateComponentApplicationMode[] = [
  "AdditiveOnPrevious",
  "MultiplicativeOnPrevious",
];

/** Plus petit `sortOrder` du tableau (composante de base : `applicationMode` toujours `null` côté API). */
export function minSortOrderAmongComponents(components: { sortOrder: number }[]): number {
  if (components.length === 0) return 1;
  return Math.min(...components.map((c) => c.sortOrder));
}

function normalizeComponentApplicationModeFromApi(
  sortOrder: number,
  minSortOrder: number,
  raw: string | null | undefined,
): TsTaxRateComponentApplicationMode | null {
  if (sortOrder === minSortOrder) {
    return null;
  }
  if (raw === "AdditiveOnPrevious" || raw === "MultiplicativeOnPrevious") {
    return raw;
  }
  if (raw === "SubtotalAndPriorComponents") {
    return "MultiplicativeOnPrevious";
  }
  if (raw === "SubtotalOnly" || raw === null || raw === undefined) {
    return "AdditiveOnPrevious";
  }
  return "AdditiveOnPrevious";
}

export interface U3110InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode?: string;
}

export interface U3110TaxRateSearchRow {
  id: string;
  code?: string;
  labelFr?: string | null;
  labelEn?: string | null;
  version: number;
  /** Régime actif (backend `active`, défaut métier true). */
  active?: boolean;
  deletedAt?: string | null;
  createdAt?: string;
}

export interface U3110TaxRateComponentApi {
  id?: string;
  sortOrder: number;
  rate: number;
  labelFr?: string | null;
  labelEn?: string | null;
  applicationMode: TsTaxRateComponentApplicationMode | null;
  version?: number;
}

/** Référence pays (GET U3110, optionnel). */
export interface U3110TaxRateCountryRef {
  id: string;
  countryNameFr?: string | null;
  countryNameEn?: string | null;
  countryName?: string | null;
}

/** Référence province / état (GET U3110, optionnel). */
export interface U3110TaxRateProvinceRef {
  id: string;
  stateNameFr?: string | null;
  stateNameEn?: string | null;
  stateName?: string | null;
}

/** GET /U3110/:id — sans organization (schéma `U3110TaxRateGetSchema`). */
export interface U3110TaxRateGet {
  id: string;
  code: string;
  labelFr?: string | null;
  labelEn?: string | null;
  version: number;
  active?: boolean;
  countryId?: string | null;
  provinceId?: string | null;
  country?: U3110TaxRateCountryRef | null;
  province?: U3110TaxRateProvinceRef | null;
  taxRateComponents: U3110TaxRateComponentApi[];
}

export interface U3110TaxRateOrganization {
  id: string;
  uuid?: string;
  organizationName?: string;
}

/** Réponse create peut inclure organization */
export interface U3110TaxRateWithOrg extends U3110TaxRateGet {
  organization?: U3110TaxRateOrganization;
}

export interface U3110TaxRateComponentFormRow {
  id?: string;
  sortOrder: number;
  rate: number;
  labelFr: string;
  labelEn: string;
  /** `null` pour la composante au plus petit `sortOrder` ; sinon une des deux valeurs enum. */
  applicationMode: TsTaxRateComponentApplicationMode | null;
  version?: number;
}

/** Pays (formulaire — autocomplétion, aligné U3120). */
export type U3110TaxRateFormCountry = {
  id: string | null;
  countryNameFr?: string | null;
  countryNameEn?: string | null;
  countryName?: string | null;
  name?: string | null;
};

/** Province (formulaire — autocomplétion). */
export type U3110TaxRateFormProvince = {
  id: string | null;
  stateNameFr?: string | null;
  stateNameEn?: string | null;
  stateName?: string | null;
  name?: string | null;
};

export function emptyU3110TaxRateFormCountry(): U3110TaxRateFormCountry {
  return { id: null, countryNameFr: "", countryNameEn: "", countryName: "", name: "" };
}

export function emptyU3110TaxRateFormProvince(): U3110TaxRateFormProvince {
  return { id: null, stateNameFr: "", stateNameEn: "", stateName: "", name: "" };
}

export type U3110TaxRateFormRow = {
  id: string;
  code: string;
  labelFr: string;
  labelEn: string;
  version: number;
  active: boolean;
  country: U3110TaxRateFormCountry;
  province: U3110TaxRateFormProvince;
  taxRateComponents: U3110TaxRateComponentFormRow[];
};

function strToForm(s: string | null | undefined): string {
  return s ?? "";
}

export function sortComponentsByOrder(components: U3110TaxRateComponentApi[]): U3110TaxRateComponentApi[] {
  return [...components].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

function normalizeCountryFromApi(row: U3110TaxRateGet | U3110TaxRateWithOrg): U3110TaxRateFormCountry {
  const nested = row.country;
  const idRaw = nested?.id ?? row.countryId ?? null;
  const id = idRaw && isValidId(String(idRaw)) ? String(idRaw) : null;
  if (!id) return emptyU3110TaxRateFormCountry();
  return {
    id,
    countryNameFr: nested?.countryNameFr ?? "",
    countryNameEn: nested?.countryNameEn ?? "",
    countryName: nested?.countryName ?? "",
    name: nested?.countryNameFr ?? nested?.countryNameEn ?? nested?.countryName ?? "",
  };
}

function normalizeProvinceFromApi(row: U3110TaxRateGet | U3110TaxRateWithOrg): U3110TaxRateFormProvince {
  const nested = row.province;
  const idRaw = nested?.id ?? row.provinceId ?? null;
  const id = idRaw && isValidId(String(idRaw)) ? String(idRaw) : null;
  if (!id) return emptyU3110TaxRateFormProvince();
  return {
    id,
    stateNameFr: nested?.stateNameFr ?? "",
    stateNameEn: nested?.stateNameEn ?? "",
    stateName: nested?.stateName ?? "",
    name: nested?.stateNameFr ?? nested?.stateNameEn ?? nested?.stateName ?? "",
  };
}

export function normalizeU3110TaxRateFromApi(row: U3110TaxRateGet | U3110TaxRateWithOrg): U3110TaxRateFormRow {
  const sorted = sortComponentsByOrder(row.taxRateComponents ?? []);
  const minSo = minSortOrderAmongComponents(sorted);
  return {
    id: row.id,
    code: row.code ?? "",
    labelFr: strToForm(row.labelFr),
    labelEn: strToForm(row.labelEn),
    version: row.version ?? 0,
    active: row.active !== false,
    country: normalizeCountryFromApi(row),
    province: normalizeProvinceFromApi(row),
    taxRateComponents: sorted.map((c) => ({
      id: c.id,
      sortOrder: c.sortOrder,
      rate: c.rate,
      labelFr: strToForm(c.labelFr),
      labelEn: strToForm(c.labelEn),
      applicationMode: normalizeComponentApplicationModeFromApi(c.sortOrder, minSo, c.applicationMode as string | null | undefined),
      version: c.version,
    })),
  };
}

/** @param isMinimalSortOrder `true` pour la première ligne (seule composante ou plus petit ordre à la création). */
export function createEmptyComponentRow(sortOrder: number, isMinimalSortOrder: boolean): U3110TaxRateComponentFormRow {
  return {
    sortOrder,
    rate: 0,
    labelFr: "",
    labelEn: "",
    applicationMode: isMinimalSortOrder ? null : "AdditiveOnPrevious",
  };
}

/**
 * Réassigne `sortOrder` à 1..n selon l’ordre courant du tableau (après suppression).
 * La première ligne devient la composante de base (`applicationMode: null`).
 */
export function resequenceTaxRateComponents(components: U3110TaxRateComponentFormRow[]): U3110TaxRateComponentFormRow[] {
  return components.map((c, i) => ({
    ...c,
    sortOrder: i + 1,
    applicationMode: i === 0 ? null : c.applicationMode ?? "AdditiveOnPrevious",
  }));
}

export function formToSearchRow(f: U3110TaxRateFormRow): U3110TaxRateSearchRow {
  return {
    id: f.id,
    code: f.code,
    labelFr: f.labelFr || null,
    labelEn: f.labelEn || null,
    version: f.version,
    active: f.active,
  };
}

export function createEmptyU3110FormRow(): U3110TaxRateFormRow {
  return {
    id: "",
    code: "",
    labelFr: "",
    labelEn: "",
    version: 0,
    active: true,
    country: emptyU3110TaxRateFormCountry(),
    province: emptyU3110TaxRateFormProvince(),
    taxRateComponents: [createEmptyComponentRow(1, true)],
  };
}

function nullIfEmpty(s: string): string | null {
  const t = s.trim();
  return t === "" ? null : t;
}

export interface U3110TaxRateComponentCreateBody {
  sortOrder: number;
  rate: number;
  labelFr?: string | null;
  labelEn?: string | null;
  applicationMode: TsTaxRateComponentApplicationMode | null;
}

export interface U3110TaxRateComponentUpdateBody extends U3110TaxRateComponentCreateBody {
  id?: string;
  version?: number;
}

export interface U3110TaxRateCreateBody {
  code: string;
  labelFr?: string | null;
  labelEn?: string | null;
  active: boolean;
  countryId?: string | null;
  provinceId?: string | null;
  taxRateComponents: U3110TaxRateComponentCreateBody[];
}

export interface U3110TaxRateUpdateBody {
  id: string;
  code: string;
  labelFr?: string | null;
  labelEn?: string | null;
  version: number;
  active: boolean;
  countryId?: string | null;
  provinceId?: string | null;
  taxRateComponents: U3110TaxRateComponentUpdateBody[];
}

export interface U3110TaxRateDeleteBody {
  id: string;
  version: number;
}

function jurisdictionIdsFromForm(data: U3110TaxRateFormRow): { countryId: string | null; provinceId: string | null } {
  const cid = data.country?.id && isValidId(data.country.id) ? data.country.id : null;
  const pid = data.province?.id && isValidId(data.province.id) ? data.province.id : null;
  return { countryId: cid, provinceId: pid };
}

export function buildCreateBody(data: U3110TaxRateFormRow): U3110TaxRateCreateBody {
  const minSo = minSortOrderAmongComponents(data.taxRateComponents);
  const { countryId, provinceId } = jurisdictionIdsFromForm(data);
  return {
    code: data.code.trim(),
    labelFr: nullIfEmpty(data.labelFr),
    labelEn: nullIfEmpty(data.labelEn),
    active: data.active,
    countryId,
    provinceId,
    taxRateComponents: data.taxRateComponents.map((c) => ({
      sortOrder: c.sortOrder,
      rate: c.rate,
      labelFr: nullIfEmpty(c.labelFr),
      labelEn: nullIfEmpty(c.labelEn),
      applicationMode: c.sortOrder === minSo ? null : c.applicationMode ?? "AdditiveOnPrevious",
    })),
  };
}

export function buildUpdateBody(data: U3110TaxRateFormRow): U3110TaxRateUpdateBody {
  const minSo = minSortOrderAmongComponents(data.taxRateComponents);
  const { countryId, provinceId } = jurisdictionIdsFromForm(data);
  return {
    id: data.id,
    code: data.code.trim(),
    labelFr: nullIfEmpty(data.labelFr),
    labelEn: nullIfEmpty(data.labelEn),
    version: data.version,
    active: data.active,
    countryId,
    provinceId,
    taxRateComponents: data.taxRateComponents.map((c) => {
      const base: U3110TaxRateComponentUpdateBody = {
        sortOrder: c.sortOrder,
        rate: c.rate,
        labelFr: nullIfEmpty(c.labelFr),
        labelEn: nullIfEmpty(c.labelEn),
        applicationMode: c.sortOrder === minSo ? null : c.applicationMode ?? "AdditiveOnPrevious",
      };
      if (c.id) {
        base.id = c.id;
        if (c.version !== undefined) {
          base.version = c.version;
        }
      }
      return base;
    }),
  };
}

export function buildDeleteBody(data: U3110TaxRateFormRow): U3110TaxRateDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const U3110_ERROR_CODE_DUPLICATE = "BU311001";
export const U3110_ERROR_SORT_ORDER_DUP = "BU311002";
export const U3110_ERROR_COMPONENT_NOT_FOUND = "BU311003";
export const U3110_ERROR_ENTITY_ID_MISMATCH = "B00008";

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3110TaxRateCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3110TaxRateUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3110TaxRateDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3110_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors["code"] = t("errCodeDuplicate", { ns: nameSpace });
      break;
    case U3110_ERROR_SORT_ORDER_DUP:
      errorResult.otherErrors["taxRateComponents"] = t("errSortOrderDuplicate", { ns: nameSpace });
      break;
    case U3110_ERROR_COMPONENT_NOT_FOUND:
      errorResult.fieldErrors["taxRateComponents"] = t("errComponentNotFound", { ns: nameSpace });
      break;
    case U3110_ERROR_ENTITY_ID_MISMATCH:
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace }),
        text: t("errEntityIdMismatch", { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
